import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CTAButtonProps {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  subtext?: string;
  href?: string;
  onClick?: () => void;
  icon?: boolean;
  fullWidth?: boolean;
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  variant = 'primary',
  size = 'lg',
  children,
  subtext,
  href = '/diagnostic',
  onClick,
  icon = false,
  fullWidth = false,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(href);
    }
  };

  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl',
    secondary: 'bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-800 hover:to-slate-900',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  };

  return (
    <div className={`flex flex-col items-center ${fullWidth ? 'w-full' : ''}`}>
      <motion.button
        onClick={handleClick}
        className={`
          ${variants[variant]}
          ${sizes[size]}
          ${fullWidth ? 'w-full' : ''}
          font-semibold rounded-xl
          transition-all duration-300
          flex items-center justify-center gap-3
          focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-opacity-50
        `}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={typeof children === 'string' ? children : 'Bouton d\'action'}
      >
        {children}
        {icon && <ArrowRight size={size === 'xl' ? 28 : size === 'lg' ? 24 : 20} />}
      </motion.button>
      {subtext && (
        <p className="mt-3 text-sm text-slate-400 text-center">
          {subtext}
        </p>
      )}
    </div>
  );
};
